/**
 * Ce que la plateforme sait faire — écrit pour être montré, non pour être
 * consulté par un développeur.
 *
 * **Le vocabulaire est celui du métier.** Un économiste de la construction
 * connaît un CCTP, une DPGF, un DWG et une réserve ; il n'a pas à connaître un
 * graphe d'orchestration ni un catalogue d'outils. Ce qui est technique est
 * traduit ; ce qui est du métier est gardé tel quel — traduire « CCTP » serait
 * aussi condescendant qu'inutile.
 *
 * **Les chiffres viennent du portail, pas d'ici.** Le nombre d'agents, celui
 * des outils et l'ordre des étapes sont lus sur `/api/v1/capacites`, qui les
 * tient du catalogue réel. Écrits en dur, ils auraient été justes le jour de
 * leur saisie : un outil ajouté, et la page aurait continué d'annoncer
 * l'ancien monde sans que rien n'échoue.
 *
 * **Ce que la page ne connaît pas, elle le montre quand même.** Les familles
 * d'outils ci-dessous sont une mise en forme métier ; si le catalogue gagne un
 * outil qu'aucune famille ne réclame, il paraît dans « Autres capacités »
 * plutôt que de disparaître. Une page vitrine qui masque ce qu'elle ignore
 * finit par décrire un produit qui n'existe plus.
 */
import { api } from "../api/client";
import { Chargement, Echec } from "../composants/etats";
import { useDonnees } from "../composants/donnees";

interface Agent {
  role: string;
  libelle: string;
  outils: string[];
}

interface Vue {
  agents: Agent[];
  outils: string[];
  outils_de_preparation: string[];
  outils_sans_agent: string[];
  etapes: string[];
}

/** Ce que chaque agent fait, dit au client.
 *
 * `mission` répond à « qu'est-ce qu'on lui demande ? ». `interdit` répond à
 * « qu'est-ce qui l'empêche de déraper ? » — et c'est la moitié qui compte :
 * la valeur de cette chaîne tient à ce que chacun **ne peut pas** faire. */
const METIERS: Record<
  string,
  { titre: string; question: string; mission: string; interdit: string }
> = {
  topologique: {
    titre: "Repérage",
    question: "Où est ce local, et que touche-t-il ?",
    mission:
      "Retrouve un local dans les plans et en rend le contour, les parois qui "
      + "le bordent, les baies qu'elles portent et les locaux mitoyens. Quand "
      + "la géométrie reste muette, il regarde le dessin — un nom posé hors de "
      + "son contour ne se lit sur aucune coordonnée.",
    interdit:
      "Il ne calcule aucune quantité. Ce qu'il voit à l'écran est une "
      + "observation, jamais une mesure.",
  },
  normatif: {
    titre: "Prescriptions",
    question: "Que dit la pièce écrite ?",
    mission:
      "Interroge le CCTP indexé : ce qui est prescrit, les caractéristiques "
      + "d'un matériau, et surtout le mode de métré — une baie se déduit-elle, "
      + "et à partir de quelle surface. Il consulte aussi la DPGF pour "
      + "confronter ce que le client a chiffré à ce qu'il a prescrit.",
    interdit:
      "Il lit, il n'écrit pas. La DPGF est consultée en lecture seule, et le "
      + "rapprochement avec le métré demande l'accord du client.",
  },
  geometrique: {
    titre: "Calcul",
    question: "Combien cela fait-il ?",
    mission:
      "Reçoit les contours relevés et la règle de déduction trouvée, choisit "
      + "la formule, et fait calculer. Chaque quantité qu'il rend est rejouée "
      + "et confrontée à son résultat avant d'être retenue.",
    interdit:
      "Il ne calcule jamais de tête. Un nombre qu'aucun outil n'a produit "
      + "n'entre pas au métré — c'est la règle qui fonde toute la chaîne.",
  },
  analyste: {
    titre: "Analyse",
    question: "Et si l'on croisait tout le dossier ?",
    mission:
      "Le seul qui exécute du code, dans un bac à sable sans réseau. Il croise "
      + "vingt plans, cherche les locaux qui s'écartent de leur surface "
      + "écrite, éprouve une hypothèse — ce qu'aucun outil du catalogue ne "
      + "couvre.",
    interdit:
      "Ce qu'il produit est marqué « invérifiable » au journal : non pas faux, "
      + "mais non confirmé. Il n'écrit rien au métré.",
  },
  redacteur: {
    titre: "Rédaction",
    question: "Comment cela entre-t-il dans le classeur ?",
    mission:
      "Porte les quantités établies dans le métré du client, en respectant sa "
      + "structure : un local déjà prévu se met à jour, il ne se rajoute pas. "
      + "Mise en forme, couleurs et formules sont conservées.",
    interdit:
      "Il ne recalcule rien, et ne peut poser aucune question — lui laisser "
      + "demander « quelle valeur en E59 ? » ferait entrer un chiffre qui n'a "
      + "traversé ni relevé, ni lecture, ni arbitrage.",
  },
};

/** Les outils rangés par usage, et non par module.
 *
 * Un client ne demande pas « quels outils DXF avez-vous », il demande « savez-
 * vous lire mes plans ». Les noms techniques restent visibles au survol : le
 * support en a besoin, et les masquer donnerait une vitrine invérifiable. */
const FAMILLES: { titre: string; propos: string; outils: string[] }[] = [
  {
    titre: "Lire les plans",
    propos:
      "Déplier un plan d'architecte, en tirer les locaux, leurs contours, "
      + "leurs hauteurs, les murs et les baies. DWG, DXF, PDF et maquette IFC.",
    outils: [
      "Catalog_Plan_Sources", "Resolve_Room_Sources", "Survey_Room",
      "DXF_block_flattener", "DXF_Layer_Mapper", "DXF_Solid_Geometry_Extractor",
      "DXF_Room_Builder", "Resolve_Room_Boundary", "Split_Zone_By_Partition",
      "Get_Room_Height", "Get_Walls_For_Room", "Find_Openings_In_Wall",
      "Survey_Openings_For_Room", "Get_Adjoining_Rooms", "Look_At_Plan",
      "IFC_Model_Extractor", "PDF_Vector_Extractor", "Plan_Coordinate_Aligner",
    ],
  },
  {
    titre: "Consulter les pièces écrites",
    propos:
      "Chercher ce que le CCTP prescrit, ce qu'il impose, et ce que la DPGF "
      + "du client a retenu.",
    outils: [
      "Search_CCTP_Rule", "Check_Material_Specification", "Verify_Deduction_Rule",
      "Resolve_Work_Scope", "Query_Spec_Graph", "Consult_DPGF",
    ],
  },
  {
    titre: "Calculer",
    propos:
      "Surfaces nettes, linéaires, parois baies déduites, conversions "
      + "d'unités. Déterministes et rejouables — deux fois la même donnée "
      + "donne deux fois le même nombre.",
    outils: [
      "Calculate_Net_Area", "Calculate_Net_Perimeter", "Calculate_Wall_Area",
      "Calculate_Room_Wall_Area", "Convert_Units",
    ],
  },
  {
    titre: "Contrôler et tracer",
    propos:
      "Rejouer un calcul pour le confronter à son résultat, signaler un local "
      + "qu'on ne peut pas chiffrer, poser une question au métreur plutôt que "
      + "de trancher à sa place.",
    outils: ["Log_Calculation_Trace", "Flag_Discrepancy", "Ask_User"],
  },
  {
    titre: "Écrire le métré",
    propos:
      "Charger le classeur du client, renseigner les grandeurs, ajouter les "
      + "locaux absents, enregistrer sans rien abîmer.",
    outils: [
      "Load_Excel_Template", "Update_Room_Metrics", "Add_Calculated_Row",
      "Export_Final_Excel",
    ],
  },
  {
    titre: "Explorer",
    propos:
      "Un bac à sable isolé, sans réseau, où l'on croise et l'on éprouve. Ce "
      + "qui en sort est marqué comme non confirmé.",
    outils: [
      "Python_REPL", "Reset_Python_Session", "Attach_To_REPL",
      "Save_REPL_Artifact",
    ],
  },
  {
    titre: "Préparer le dossier",
    propos:
      "Convertir les plans et indexer les pièces écrites. Ces deux capacités "
      + "ne sont confiées à aucun agent : elles réécrivent les sources, et un "
      + "agent qui pourrait réécrire ses sources ne prouverait plus rien.",
    outils: ["DWG_to_DXF_converter", "CCTP_Vector_Embedder"],
  },
];

/** Ce que la chaîne garantit, et pourquoi c'est vendable.
 *
 * Chaque ligne correspond à un mécanisme réel, pas à une intention. */
const GARANTIES = [
  {
    titre: "Aucun chiffre inventé",
    texte:
      "Toute valeur portée au métré provient d'un outil de calcul. Une case "
      + "vide se voit et se corrige ; un chiffre faux ne se voit pas.",
  },
  {
    titre: "Chaque calcul est rejoué",
    texte:
      "L'opération est recalculée et confrontée au résultat annoncé. Si les "
      + "deux divergent, la quantité ne passe pas.",
  },
  {
    titre: "Les réserves remontent intactes",
    texte:
      "« Contour reconstruit, à relire », « aucune règle prescrite » : ces "
      + "mentions sont prélevées à la sortie des outils, avant toute "
      + "rédaction. Personne ne peut les lisser en chemin.",
  },
  {
    titre: "La synthèse n'est pas réécrite",
    texte:
      "Le rapport final assemble ce que chaque intervenant a répondu, mot "
      + "pour mot. Une belle rédaction d'ensemble aurait pu arrondir un "
      + "nombre ou fondre deux réserves en une.",
  },
  {
    titre: "Le doute devient une question",
    texte:
      "Deux hauteurs également lisibles au plan ? Le métreur tranche, en "
      + "séance. Retenir la plus grande « par prudence » fabriquerait une "
      + "quantité que personne n'a décidée.",
  },
  {
    titre: "Chaque ligne remonte à son origine",
    texte:
      "Un contour vient d'un calque, une prescription d'un article de CCTP, "
      + "une quantité d'un calcul détaillé. Six mois plus tard, le métré se "
      + "défend encore.",
  },
];

export function Capacites() {
  const vue = useDonnees<{ donnees: Vue }>(
    () => api.lire("/api/v1/capacites"),
    [],
  );

  const donnees = vue.donnees?.donnees;
  const rangés = new Set(FAMILLES.flatMap((f) => f.outils));
  const orphelins = (donnees?.outils ?? []).filter((o) => !rangés.has(o));

  return (
    <div className="ecran">
      <div className="ecran__tete">
        <div className="rangee rangee--espacee rangee--bas">
          <div>
            <h1 className="ecran__titre">Capacités</h1>
            <div className="ecran__compte">
              Ce que la plateforme sait faire, et ce qu'elle refuse de faire
            </div>
          </div>
        </div>
      </div>

      <div className="ecran__corps">
        {vue.charge && !donnees ? (
          <Chargement lignes={6} />
        ) : vue.erreur ? (
          <Echec erreur={vue.erreur} reessayer={vue.recharger} />
        ) : donnees ? (
          <div className="vitrine">
            <Promesse donnees={donnees} />
            <Parcours />
            <Equipe agents={donnees.agents} etapes={donnees.etapes} />
            <Chaine etapes={donnees.etapes} />
            <Pouvoirs />
            <Outillage orphelins={orphelins} total={donnees.outils.length} />
            <Garanties />
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** L'accroche, et quatre chiffres qui la soutiennent. */
function Promesse({ donnees }: { donnees: Vue }) {
  const chiffres = [
    { valeur: donnees.agents.length, libelle: "intervenants spécialisés" },
    { valeur: donnees.outils.length, libelle: "capacités au catalogue" },
    { valeur: donnees.etapes.length, libelle: "étapes possibles" },
    { valeur: donnees.outils_sans_agent.length, libelle: "confiées à personne" },
  ];

  return (
    <section className="vitrine__accroche">
      <p className="vitrine__surtitre">Du DCE au métré chiffré</p>
      <h2 className="vitrine__titre">
        Un métré qui se défend ligne par ligne, six mois après sa remise.
      </h2>
      <p className="vitrine__propos">
        La plateforme reçoit un dossier de consultation, en lit les plans et les
        pièces écrites, et porte les quantités dans le classeur du client. Ce
        qui la distingue n'est pas d'aller vite : c'est que chaque nombre
        remis puisse être remonté jusqu'à son calque, son article de CCTP et
        son calcul — et que ce dont elle doute soit dit, plutôt que lissé.
      </p>
      <div className="vitrine__chiffres">
        {chiffres.map((c) => (
          <div key={c.libelle} className="vitrine__chiffre">
            <span className="vitrine__nombre">{c.valeur}</span>
            <span className="vitrine__legende">{c.libelle}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

const ETAPES_AFFAIRE = [
  {
    titre: "Le dossier arrive",
    texte:
      "Les pièces sont rattachées depuis l'espace du cabinet — OneDrive ou "
      + "SharePoint. Rien n'est recopié à la main.",
  },
  {
    titre: "Chaque pièce est versionnée",
    texte:
      "Une nouvelle version ne remplace pas la précédente : elle s'empile. "
      + "C'est ce qui rend un métré remis en juin encore vérifiable en "
      + "décembre.",
  },
  {
    titre: "Un instantané fige la base",
    texte:
      "L'ensemble exact des documents qui servent au chiffrage est arrêté et "
      + "nommé. Le métré cite cet instantané.",
  },
  {
    titre: "La mission chiffre",
    texte:
      "Les intervenants se relaient sur la question posée : repérer, "
      + "consulter, calculer, rédiger. Le métreur tranche ce qui doit l'être.",
  },
  {
    titre: "Le métré part au client",
    texte:
      "Le classeur du client est renseigné dans sa propre structure, ses "
      + "formules intactes, accompagné de ses réserves.",
  },
];

/** Le parcours d'une affaire, en CSS plutôt qu'en image.
 *
 * Un schéma dessiné en SVG aurait une largeur fixe et un texte qui ne se
 * cherche pas : celui-ci se replie sur un écran étroit, se lit à la voix, et
 * se traduit. La règle vaut tant que le schéma reste une **suite** ; dès qu'il
 * bifurque, le dessin reprend ses droits — voir la chaîne d'une mission. */
function Parcours() {
  return (
    <section className="vitrine__section">
      <h3 className="vitrine__rubrique">Le parcours d'une affaire</h3>
      <ol className="ruban">
        {ETAPES_AFFAIRE.map((etape, rang) => (
          <li key={etape.titre} className="ruban__etape">
            <span className="ruban__rang" aria-hidden="true">{rang + 1}</span>
            <h4 className="ruban__titre">{etape.titre}</h4>
            <p className="ruban__texte">{etape.texte}</p>
          </li>
        ))}
      </ol>
    </section>
  );
}

function Equipe({ agents, etapes }: { agents: Agent[]; etapes: string[] }) {
  // **Dans l'ordre de la mission, non dans celui du catalogue.** La page
  // raconte une chaîne ; présenter le calcul avant les prescriptions ferait
  // lire l'histoire à l'envers. Un rôle que la chaîne ne nomme pas passe en
  // fin de liste plutôt que de disparaître.
  const rang = (role: string) => {
    const trouve = etapes.indexOf(role);
    return trouve === -1 ? etapes.length : trouve;
  };
  const ordonnes = [...agents].sort((a, b) => rang(a.role) - rang(b.role));

  return (
    <section className="vitrine__section">
      <h3 className="vitrine__rubrique">Les intervenants</h3>
      <p className="vitrine__intro">
        Cinq spécialités, et aucune n'est polyvalente. Un intervenant qui
        verrait toutes les capacités choisirait mal, et sa réponse ne dirait
        plus d'où elle vient. C'est le cloisonnement qui rend le résultat
        opposable.
      </p>
      <div className="metiers">
        {ordonnes.map((agent) => {
          const metier = METIERS[agent.role];
          return (
            <article key={agent.role} className="metier">
              <div className="metier__tete">
                <h4>{metier?.titre ?? agent.libelle}</h4>
                <span className="metier__compte">
                  {agent.outils.length} capacité
                  {agent.outils.length > 1 ? "s" : ""}
                </span>
              </div>
              <p className="metier__question">
                {metier?.question ?? agent.libelle}
              </p>
              <p className="metier__mission">{metier?.mission}</p>
              {metier && (
                <p className="metier__interdit">
                  <strong>Ce qu'il ne fait pas.</strong> {metier.interdit}
                </p>
              )}
            </article>
          );
        })}
      </div>
    </section>
  );
}

/** Les étapes d'une mission, telles que le portail les nomme, traduites.
 *
 * La clé est l'étape réelle : une étape ajoutée au moteur paraîtra ici sous
 * son nom technique plutôt que de manquer. */
const MOTS_DETAPE: Record<string, string> = {
  plan: "Cadrage de la question",
  topologique: "Repérage sur les plans",
  normatif: "Lecture des prescriptions",
  validation_preuves: "Contrôle des preuves",
  decision_preuves: "Arbitrage du métreur",
  geometrique: "Calcul des quantités",
  analyste: "Analyse complémentaire",
  evaluation: "Évaluation du résultat",
  arbitrage: "Arbitrage du métreur",
  redacteur: "Écriture au classeur",
  synthese: "Rapport et réserves",
};

/** La chaîne d'une mission, et les deux endroits où elle rend la main.
 *
 * **Dessinée en CSS, non en SVG.** Le premier jet traçait un SVG : étiré à la
 * largeur du bloc, son `viewBox` étroit grossissait tout d'un facteur trois et
 * demi — le texte débordait et l'aparté chevauchait le nom de l'étape. Un
 * schéma dont les libellés sont du texte n'a rien à gagner à devenir une image
 * : ici le texte reste du texte, il se replie, se sélectionne et se traduit.
 *
 * Ce qu'on veut faire voir n'est pas la suite — elle serait banale — mais les
 * **deux sorties latérales** : les points où la plateforme s'arrête plutôt que
 * de deviner. Ils portent donc la seule couleur du schéma. */
function Chaine({ etapes }: { etapes: string[] }) {
  // Les deux étapes d'arbitrage ne sont pas des rangs de la chaîne : ce sont
  // les sorties des deux contrôles. Les compter comme des étapes ferait
  // paraître le métreur comme un maillon, alors qu'il est l'arbitre.
  const humaines = new Set(["decision_preuves", "arbitrage"]);
  const suite = etapes.filter((etape) => !humaines.has(etape));
  const portes = new Set(["validation_preuves", "evaluation"]);

  return (
    <section className="vitrine__section">
      <h3 className="vitrine__rubrique">La chaîne d'une mission</h3>
      <p className="vitrine__intro">
        Les intervenants ne se parlent pas : ils se transmettent des dossiers
        montés par la plateforme. Le contour relevé au plan arrive au calcul
        comme une donnée, non comme une phrase à relire. Et deux contrôles
        peuvent interrompre la chaîne pour rendre la main au métreur — c'est
        là que la machine s'arrête au lieu de deviner.
      </p>

      <ol className="chaine">
        {suite.map((etape, rang) => {
          const porte = portes.has(etape);
          return (
            <li key={etape}
                className={`chaine__etape${porte ? " chaine__etape--porte" : ""}`}>
              <span className="chaine__rang" aria-hidden="true">{rang + 1}</span>
              <span className="chaine__nom">{MOTS_DETAPE[etape] ?? etape}</span>
              {porte && (
                <span className="chaine__aparte">
                  rendu au métreur si un point reste ouvert
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
}

/** Qui peut quoi. Cinq lignes, cinq colonnes, et aucune ligne pleine. */
const POUVOIRS = [
  { nom: "Lire les plans", qui: ["topologique"] },
  { nom: "Lire les pièces écrites", qui: ["normatif"] },
  { nom: "Calculer une quantité", qui: ["geometrique"] },
  { nom: "Exécuter du code", qui: ["analyste"] },
  { nom: "Écrire au classeur", qui: ["redacteur"] },
  { nom: "Signaler un blocage", qui: ["topologique", "normatif", "geometrique",
                                      "analyste", "redacteur"] },
];

function Pouvoirs() {
  const colonnes = ["topologique", "normatif", "geometrique", "analyste",
                    "redacteur"];

  return (
    <section className="vitrine__section">
      <h3 className="vitrine__rubrique">La séparation des pouvoirs</h3>
      <p className="vitrine__intro">
        Aucun intervenant n'en réunit deux. Celui qui lit les plans ne calcule
        pas ; celui qui calcule n'écrit pas au classeur ; celui qui exécute du
        code n'écrit nulle part. Une seule ligne est commune, et elle l'est
        volontairement : n'importe lequel peut dire qu'un local est
        inchiffrable, et réserver ce droit à un seul obligerait les autres à
        taire ce qu'ils voient.
      </p>
      <div className="defilant">
        <table className="pouvoirs">
          <thead>
            <tr>
              <th scope="col">Capacité</th>
              {colonnes.map((c) => (
                <th key={c} scope="col">{METIERS[c]?.titre ?? c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {POUVOIRS.map((ligne) => (
              <tr key={ligne.nom}>
                <th scope="row">{ligne.nom}</th>
                {colonnes.map((c) => {
                  const a = ligne.qui.includes(c);
                  return (
                    <td key={c} className={a ? "pouvoirs__oui" : ""}>
                      {/* Jamais la couleur seule : le mot survit à une
                          impression en noir et blanc. */}
                      <span className="pouvoirs__marque" aria-hidden="true">
                        {a ? "●" : "·"}
                      </span>
                      <span className="pouvoirs__mot">{a ? "oui" : "non"}</span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Outillage({ orphelins, total }: { orphelins: string[]; total: number }) {
  return (
    <section className="vitrine__section">
      <h3 className="vitrine__rubrique">
        Les capacités, par usage <span className="vitrine__total">{total}</span>
      </h3>
      <div className="familles">
        {FAMILLES.map((famille) => (
          <article key={famille.titre} className="famille">
            <div className="famille__tete">
              <h4>{famille.titre}</h4>
              <span className="famille__compte">{famille.outils.length}</span>
            </div>
            <p className="famille__propos">{famille.propos}</p>
            {/* Les noms techniques restent atteignables : le support en a
                besoin, et une vitrine qui ne se vérifie pas n'engage rien. */}
            <details className="famille__detail">
              <summary>Voir le détail</summary>
              <ul>
                {famille.outils.map((outil) => (
                  <li key={outil} className="mono">{outil}</li>
                ))}
              </ul>
            </details>
          </article>
        ))}

        {orphelins.length > 0 && (
          <article className="famille famille--neuve">
            <div className="famille__tete">
              <h4>Autres capacités</h4>
              <span className="famille__compte">{orphelins.length}</span>
            </div>
            <p className="famille__propos">
              Au catalogue, pas encore rangées dans cette présentation.
            </p>
            <details className="famille__detail">
              <summary>Voir le détail</summary>
              <ul>
                {orphelins.map((outil) => (
                  <li key={outil} className="mono">{outil}</li>
                ))}
              </ul>
            </details>
          </article>
        )}
      </div>
    </section>
  );
}

function Garanties() {
  return (
    <section className="vitrine__section">
      <h3 className="vitrine__rubrique">Ce qui rend un métré défendable</h3>
      <div className="garanties">
        {GARANTIES.map((g) => (
          <div key={g.titre} className="garantie">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
              <path d="m4 12.5 5.2 5.2L20 7" />
            </svg>
            <div>
              <h4>{g.titre}</h4>
              <p>{g.texte}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
